import React, { useState, useRef, useEffect } from 'react';
import { Bot, User, Camera, Send, Loader2, Eye } from 'lucide-react';
import { api } from '../../lib/api';

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
};

interface ChatMessage {
  role: 'user' | 'ai';
  text: string;
  images?: string[]; 
  screenshot?: string;
  isCodeApplied?: boolean;
  isError?: boolean;
}

interface AiSidebarProps {
  getCurrentHtml: () => string;
  pushToHistory: (html: string, shouldReloadIframe?: boolean) => void;
  setIframeKey: React.Dispatch<React.SetStateAction<number>>;
  captureSiteScreenshot: () => Promise<string | null>;
  activeTab: string;
}

export const AiSidebar: React.FC<AiSidebarProps> = ({ getCurrentHtml, pushToHistory, setIframeKey, captureSiteScreenshot, activeTab }) => {
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [thinkingStep, setThinkingStep] = useState<string>('');
  const [chatImages, setChatImages] = useState<File[]>([]);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory, activeTab]);

  const handleCopilotSubmit = async () => {
    if (!chatInput.trim() && chatImages.length === 0) return;
    const userText = chatInput;
    const currentImages = [...chatImages];
    setChatInput('');
    setChatImages([]);
    const imagePreviews = await Promise.all(currentImages.map(img => fileToBase64(img)));
    setChatHistory(prev => [...prev, { role: 'user', text: userText, images: imagePreviews }]);
    setIsAiThinking(true);
    setThinkingStep('Processando...');
    try {
      const screenshot = await captureSiteScreenshot();
      const currentHtml = getCurrentHtml();
      
      // A chamada ao Gemini saiu do NAVEGADOR (item B-07).
      //
      // Antes: `new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY })`, com a
      // chave inlinada no bundle pelo `define` do vite.config.ts — em texto puro,
      // extraível por qualquer visitante com o DevTools aberto.
      //
      // Agora o navegador manda as partes para a API, que fala com o Gemini com a
      // chave que só ela tem, e registra o uso para atribuir custo por cliente.
      const partes: Array<
        { tipo: 'texto'; texto: string } | { tipo: 'imagem'; mime: string; base64: string }
      > = [
        {
          tipo: 'texto',
          texto: `CÓDIGO ATUAL: ${currentHtml.substring(0, 20000)}`,
        },
        { tipo: 'texto', texto: `Pedido: ${userText}` },
      ];
      if (screenshot) {
        partes.push({
          tipo: 'imagem',
          mime: 'image/jpeg',
          base64: screenshot.split(',')[1] ?? '',
        });
      }
      for (const img of currentImages) {
        const b64 = await fileToBase64(img);
        partes.push({ tipo: 'imagem', mime: img.type, base64: b64.split(',')[1] ?? '' });
      }

      // 130 s, e não os 30 s padrão do cliente: a API espera até 100 s pelo
      // provedor, e com o navegador desistindo antes o editor mostraria "a IA
      // não respondeu" numa geração que terminou, gastou os tokens e foi
      // registrada como sucesso. Quem desiste primeiro tem de ser quem sabe o
      // que aconteceu.
      const resposta = await api.chamar(
        'ia.gerar',
        { corpo: { tarefa: 'editar-site', partes } },
        { timeoutMs: 130_000 },
      );

      // O servidor já extraiu o bloco ```html — a tela não reparseia string.
      if (resposta.html) {
        pushToHistory(resposta.html, true);
        setChatHistory(prev => [...prev, { role: 'ai', text: "Ajustes aplicados!", isCodeApplied: true }]);
      } else {
        setChatHistory(prev => [...prev, { role: 'ai', text: resposta.texto }]);
      }
    } catch (error) {
      // A mensagem do servidor chega até a tela em vez de virar "Erro na IA".
      // Sem a chave configurada, sem cota ou com o provedor fora do ar, o
      // operador precisa saber QUAL das três aconteceu.
      const detalhe = error instanceof Error ? error.message : 'Erro na IA.';
      console.error('[IA]', error);
      setChatHistory(prev => [...prev, { role: 'ai', text: detalhe, isError: true }]);
    } finally { setIsAiThinking(false); setThinkingStep(''); }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 p-5 overflow-y-auto space-y-6">
         {chatHistory.length === 0 && (
           <div className="text-center py-12">
              <div className="w-16 h-16 bg-indigo-50 text-indigo-600 rounded-3xl flex items-center justify-center mx-auto mb-4 rotate-3 shadow-inner"><Eye size={32} /></div>
              <h3 className="font-extrabold text-slate-800 text-lg">IA com Memória Ativa</h3>
              <p className="text-sm text-slate-500 mt-2 px-6 leading-relaxed">Eu lembro de cada alteração. Use as setas no topo para voltar ou adiantar versões.</p>
           </div>
         )}
         {chatHistory.map((msg, idx) => (
           <div key={idx} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-sm ${msg.role === 'user' ? 'bg-slate-100 text-slate-500' : msg.isError ? 'bg-red-500 text-white' : 'bg-indigo-600 text-white'}`}>{msg.role === 'user' ? <User size={18} /> : <Bot size={18} />}</div>
              <div className={`max-w-[85%] rounded-2xl p-4 text-sm shadow-sm ${msg.role === 'user' ? 'bg-slate-50 border border-slate-100 text-slate-800' : 'bg-indigo-600 text-white'}`}>
                 {msg.images?.map((img, i) => <img key={i} src={img} className="w-full h-28 object-cover rounded-xl mb-2" />)}
                 <p className="whitespace-pre-wrap leading-relaxed">{msg.text}</p>
              </div>
           </div>
         ))}
         {isAiThinking && <div className="flex gap-3 text-sm text-indigo-600 font-bold"><Loader2 size={16} className="animate-spin" /> {thinkingStep}</div>}
         <div ref={chatEndRef} />
      </div>
      <div className="p-4 border-t border-slate-100 bg-white">
         <div className="flex items-end gap-2 bg-slate-50 border border-slate-200 rounded-2xl p-2">
            <button onClick={() => chatFileInputRef.current?.click()} className="p-2.5 text-slate-400 hover:text-indigo-600"><Camera size={20} /></button>
            <input type="file" ref={chatFileInputRef} className="hidden" accept="image/*" multiple onChange={e => e.target.files && setChatImages(prev => [...prev, ...Array.from(e.target.files!)])} />
            <textarea value={chatInput} onChange={e => setChatInput(e.target.value)} placeholder="Peça uma mudança..." className="flex-1 bg-transparent border-none focus:ring-0 text-sm p-2 resize-none" rows={1} />
            <button onClick={handleCopilotSubmit} className="p-2.5 bg-indigo-600 text-white rounded-xl"><Send size={18} /></button>
         </div>
      </div>
    </div>
  );
};
